package com.tovtalk.ui.screen.profile

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.tovtalk.data.local.TokenManager
import com.tovtalk.di.koinInject
import com.tovtalk.di.disposableKoinInject
import com.tovtalk.domain.entity.FeedItem
import com.tovtalk.domain.repository.AdsRepository
import com.tovtalk.presentation.viewmodel.profile.ProfileViewModel
import com.tovtalk.di.koinInject
import com.tovtalk.presentation.viewmodel.auth.AuthSheetController
import com.tovtalk.ui.components.ReelFeedContent
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.StringKeys
import com.tovtalk.utils.strings.StringProvider

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProfileFeedScreen(
    userId: Int,
    startPostId: Int,
    onNavigateBack: () -> Unit,
    onNavigateToProfile: (Int) -> Unit,
    onNavigateToMyProfile: () -> Unit = {},
    onNavigateToComments: (Int) -> Unit = {},
    onNavigateToSettings: (() -> Unit)? = null,
    onFollowUser: (Int) -> Unit,
    showBackButton: Boolean = true,
    onHashtagClick: (String) -> Unit = {},
    onPostVisible: ((Int) -> Unit)? = null,
    onViewCountReached: ((Int) -> Unit)? = null,
    onNavigateToLogin: (() -> Unit)? = null,
    viewModel: ProfileViewModel = disposableKoinInject()
) {
    val reelsState by viewModel.reelsState.collectAsState()
    val feedItems by viewModel.feedItemsState.collectAsState()
    val currentUserId by viewModel.currentUserId.collectAsState()
    val authState by TokenManager.authStateFlow.collectAsState()
    val isGuestMode = authState.isGuestMode
    val authSheetController: AuthSheetController = koinInject()
    val profileReelsKashrutRestricted by viewModel.profileReelsKashrutRestricted.collectAsState()
    val adsRepository: AdsRepository = koinInject()
    LaunchedEffect(userId) {
        com.tovtalk.utils.logger.Logger.d("FEED_DEBUG: loadReels userId=$userId startPostId=$startPostId", "FEED_DEBUG")
        // Use stale-while-revalidate: show cached data immediately, refresh in background
        viewModel.loadReelsWithCacheIfAvailable(userId)
    }

    val initialPage = remember(feedItems, startPostId) {
        val idx = feedItems.indexOfFirst { it is FeedItem.VideoItem && it.post.id == startPostId }
        val firstIds = feedItems.filterIsInstance<FeedItem.VideoItem>().take(5).map { it.post.id }
        com.tovtalk.utils.logger.Logger.d("FEED_DEBUG: initialPage: startPostId=$startPostId idx=$idx count=${feedItems.size} firstIds=$firstIds", "FEED_DEBUG")
        idx.coerceAtLeast(0)
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(TovTalkTheme.colors.videoBackground)
    ) {
        when {
            reelsState.isLoading && reelsState.items.isEmpty() -> {
                com.tovtalk.ui.components.TovTalkFullScreenLoading()
            }
            !profileReelsKashrutRestricted && reelsState.items.isEmpty() -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = StringProvider.get("profile_no_posts"),
                        color = TovTalkTheme.colors.onBrand
                    )
                }
            }
            profileReelsKashrutRestricted && userId != currentUserId -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(TovTalkTheme.spacing.md),
                        modifier = Modifier.padding(TovTalkTheme.spacing.xl)
                    ) {
                        Text(
                            text = StringProvider.get(StringKeys.KASHRUT_PROFILE_BLOCK_MESSAGE),
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.onBrand,
                            textAlign = TextAlign.Center
                        )
                        onNavigateToSettings?.let { goToSettings ->
                            Button(
                                onClick = goToSettings,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = TovTalkTheme.colors.brand,
                                    contentColor = TovTalkTheme.colors.onBrand
                                )
                            ) {
                                Text(StringProvider.get(StringKeys.KASHRUT_UPDATE_LEVEL))
                            }
                        }
                    }
                }
            }
            else -> {
                ReelFeedContent(
                    feedItems = feedItems,
                    initialPage = initialPage,
                    adsRepository = adsRepository,
                    targetPostId = startPostId,
                    currentUserId = currentUserId,
                    isGuestMode = isGuestMode,
                    onLikePost = { id, isLiked -> viewModel.likePost(id, isLiked) },
                    onSavePost = { id, isSaved -> viewModel.savePost(id, isSaved) },
                    onFollowUser = { onFollowUser(it) },
                    onCommentAdded = { viewModel.incrementCommentCount(it) },
                    onUserClick = { postUserId, curUserId, _ ->
                        // Use screen userId (artist we're viewing) so navigation is correct on iOS
                        if (userId == curUserId) onNavigateToMyProfile()
                        else onNavigateToProfile(userId)
                    },
                    onNavigateToProfile = onNavigateToProfile,
                    onHashtagClick = onHashtagClick,
                    onPageChanged = { onPostVisible?.invoke(it) },
                    onViewCountReached = { onViewCountReached?.invoke(it) },
                    onLoadMore = { viewModel.loadReels(userId) },
                    onShowGuestReportPrompt = { authSheetController.open(reason = "guest_prompt_report_title") },
                )
            }
        }

        if (showBackButton) {
            IconButton(
                onClick = onNavigateBack,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .statusBarsPadding()
                    .padding(TovTalkTheme.spacing.sm)
            ) {
                Box(
                    modifier = Modifier
                        .size(TovTalkTheme.sizes.avatarMd)
                        .clip(CircleShape)
                        .background(TovTalkTheme.colors.overlayDark),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = StringProvider.get("common_back"),
                        tint = TovTalkTheme.colors.onBrand
                    )
                }
            }
        }
    }
}
